/**
 * 10.9AJ — ZONA DOS BOSSES ("Fenda dos Titãs"). Área separada, acessível só por PORTAL (teleporte):
 * uma grade de SALAS quadradas, cada uma com um boss e uma PORTA que exige nível X. Farmável (itens/
 * essência/baú) com cooldown por sala; as armas exclusivas dos bosses são quase impossíveis de cair.
 * Este módulo cuida do VISUAL (salas, portas, etiquetas, portais) e das regras geométricas (que sala
 * o player está, empurrar pra fora de sala trancada, manter dentro do círculo de luta). O Game orquestra.
 */
import * as THREE from "three";
import { groundTextures } from "./GroundTextures";
import { applyWorldStone } from "./WorldStone";
import { quality } from "../core/Quality";
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import { disposeLabel, makeLabel } from "./StructureLabel";
import { fmtDuration } from "../utils/format"; // 10.9BU
import { addPortalLabel, makePortalBase } from "./HubProps";
import { crystalStdMat, energyMat } from "./Materials";
import { makeVortexMaterial, makePortalSparks, type VortexMaterial, type PortalSparks } from "./PortalVortex";

export interface RoomDef {
  id: string;
  archetype: "alpha" | "seer";
  nameKey: string;
  tint: number;
  level: number;
  hpMult: number;
  dmgMult: number;
  /** 10.9HU — salas de PRESTÍGIO (r11+): além do nível, exigem uma FRAÇÃO da Árvore do Void
   *  fechada (0..1, sobre `VoidTreeState.totalMaxPoints`). Sem tier de item novo — a recompensa
   *  é chance bem maior do Ômega (T14) já existente, não poder além dele. */
  treePct?: number;
  /** 10.9HU — chance de drop de arma/armadura de boss desta sala (sobrescreve `bossZone.weaponDropChance`). */
  dropChance?: number;
  form?: import("../boss/BossForms").AlphaForm & import("../boss/BossForms").SeerForm; // 10.9AT
}

/** 10.9CN — parede sólida axis-aligned (fina). `lo`/`hi` = extensão no eixo LONGO. */
interface Wall { axis: "x" | "z"; pos: number; lo: number; hi: number }

export interface RoomView {
  def: RoomDef;
  cx: number;
  cz: number;
  half: number;
  floorMat: THREE.MeshStandardMaterial;
  doorMat: THREE.MeshBasicMaterial;
  labelHolder: THREE.Group;
  label: THREE.Object3D | null;
  /** 10.9BR — texto+cor do rótulo atual: só recria (canvas+textura) quando muda. */
  labelKey: string;
  // 10.9CN — SALA SELADA: paredes altas + porta no vão; ao desafiar, o portão fecha (só sai vencendo/morrendo).
  locked: boolean;
  gate: THREE.Mesh;
  gateMat: THREE.MeshBasicMaterial;
  walls: Wall[];   // paredes SEMPRE sólidas (fundo, laterais, 2 segmentos da frente)
  gateWall: Wall;  // vão da porta — sólido só quando `locked`
}

const hex = (c: number): string => "#" + c.toString(16).padStart(6, "0");

/** Portal de teleporte da Fenda dos Titãs. 10.9BP — VÓRTICE: 3 anéis Torus concêntricos inclinados
 * girando + coroa dourada no topo, sem pilares (identidade própria, diferente do arco do PvP).
 * Base/runa/rótulo vêm de HubProps. Cor configurável (portal de ida = âmbar, volta = ciano). */
export class ZonePortal {
  readonly group = new THREE.Group();
  private readonly pos: THREE.Vector3;
  private ring!: THREE.Mesh;
  private disc!: THREE.Mesh;
  private crown!: THREE.Mesh;
  private portalMat!: VortexMaterial; // 10.9GU
  private sparks!: PortalSparks;
  private readonly vortex: THREE.Mesh[] = [];
  private time = 0;

  constructor(scene: THREE.Scene, position: { x: number; z: number }, label: string, color: number, sub?: string) {
    this.pos = new THREE.Vector3(position.x, 0, position.z);
    this.buildMesh(label, color, sub);
    this.group.position.copy(this.pos);
    scene.add(this.group);
  }
  get position(): THREE.Vector3 { return this.pos; }
  setVisible(v: boolean): void { this.group.visible = v; }
  isPlayerNear(playerPos: THREE.Vector3, radius = 2.8): boolean { return playerPos.distanceTo(this.pos) <= radius; }
  update(dt: number): void {
    this.time += dt;
    this.ring.rotation.y = this.time * 0.6;
    this.portalMat.uniforms.uTime.value = this.time;
    this.sparks.update(dt);
    // vórtice: cada anel gira em eixo/velocidade própria (leitura de "fenda" viva)
    for (let i = 0; i < this.vortex.length; i++) {
      const v = this.vortex[i];
      const s = i % 2 === 0 ? 1 : -1;
      v.rotation.z = this.time * (0.5 + i * 0.35) * s;
      v.rotation.x = Math.PI / 2 + Math.sin(this.time * (0.7 + i * 0.2)) * 0.35;
    }
    this.crown.position.y = 3.75 + Math.sin(this.time * 2.2) * 0.1;
    this.crown.rotation.y = this.time * 0.9;
  }
  private buildMesh(label: string, color: number, sub?: string): void {
    // plataforma + runa (comum aos portais do hub)
    this.ring = makePortalBase(this.group, color).ring;
    // 10.9GU — vão do portal = VÓRTICE em shader (espiral girando, borda acesa, miolo escuro) + faíscas
    this.portalMat = makeVortexMaterial(color, 1);
    this.disc = new THREE.Mesh(new THREE.CircleGeometry(1.0, 28), this.portalMat);
    this.disc.position.set(0, 2.0, 0);
    this.group.add(this.disc);
    this.sparks = makePortalSparks(this.group, color, 1.7, 24, 4.4);
    // 3 anéis Torus concêntricos inclinados (roxo → dourado conforme abrem)
    const ringCols = [0x7c4dff, 0xb07cff, 0xffd54a];
    for (let i = 0; i < 3; i++) {
      const r = 1.05 + i * 0.35;
      const t = new THREE.Mesh(new THREE.TorusGeometry(r, 0.07 - i * 0.012, 8, 36), crystalStdMat(ringCols[i], ringCols[i], 1.1));
      t.position.y = 2.0;
      t.rotation.x = Math.PI / 2;
      this.group.add(t);
      this.vortex.push(t);
    }
    // coroa DOURADA flutuante no topo (motivo: bosses/titãs)
    this.crown = new THREE.Mesh(new THREE.OctahedronGeometry(0.42, 0), crystalStdMat(0xffd54a, 0xffb020, 1.0));
    this.crown.position.y = 3.75;
    this.crown.scale.set(1, 0.7, 1);
    this.group.add(this.crown);
    addPortalLabel(this.group, label, color, 5.1, sub);
  }
}

export class BossZone {
  readonly group = new THREE.Group();
  readonly rooms: RoomView[] = [];
  private readonly cfg = GameConfig.bossZone;
  private time = 0;
  // 10.9AR — "pegada de fenda": cristais flutuando, anéis do olho, materiais que pulsam
  private readonly shards: { m: THREE.Mesh; y0: number; ph: number; spin: number }[] = [];
  private readonly rings: THREE.Mesh[] = [];
  private readonly pulseMats: THREE.MeshBasicMaterial[] = [];

  constructor(scene: THREE.Scene) {
    const c = this.cfg;
    const pitch = c.roomSize + c.gap;
    const rows = Math.ceil(c.rooms.length / c.cols);
    const x0 = c.center.x - ((c.cols - 1) / 2) * pitch;
    const z0 = c.center.z - ((rows - 1) / 2) * pitch;
    c.rooms.forEach((def, i) => {
      const col = i % c.cols, row = Math.floor(i / c.cols);
      this.rooms.push(this.buildRoom(def, x0 + col * pitch, z0 + row * pitch));
    });
    // 10.9AM — a zona fica FORA do mapa (não há chão do bioma ali): chão próprio + borda luminosa
    // marcando o limite do quadrado (o clamp do mundo usa esse mesmo quadrado enquanto você está lá).
    const b = c.bounds.half;
    // 10.9AR — chão de "pedra do Void" (violeta escuro com brilho sutil), não preto chapado
    // 10.9GQ — placas de rocha do Void (textura procedural + relevo) em vez de cor lisa
    const rock = groundTextures("rock");
    const zoneMap = rock.map.clone(); zoneMap.repeat.set(Math.round(b / 3.5), Math.round(b / 3.5));
    const zoneMat = new THREE.MeshStandardMaterial({ color: 0x241a4a, roughness: 0.95, emissive: 0x2a1466, emissiveIntensity: 0.14, map: zoneMap });
    if (quality.values.normalMaps) { const n = rock.normalMap.clone(); n.repeat.copy(zoneMap.repeat); zoneMat.normalMap = n; zoneMat.normalScale.set(0.8, 0.8); }
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(b * 2, b * 2), zoneMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(c.center.x, 0, c.center.z);
    ground.receiveShadow = true;
    this.group.add(ground);
    const edgeMat = energyMat(0x7c4dff, "glow").clone(); // 10.9BQ: pulsa → clone
    this.pulseMats.push(edgeMat);
    for (const [ex, ez, w, d] of [[0, -b, b * 2, 0.5], [0, b, b * 2, 0.5], [-b, 0, 0.5, b * 2], [b, 0, 0.5, b * 2]] as const) {
      const e = new THREE.Mesh(new THREE.BoxGeometry(w, 1.6, d), edgeMat);
      e.position.set(c.center.x + ex, 0.8, c.center.z + ez);
      this.group.add(e);
    }
    this.buildRift(c);
    this.group.visible = false;
    scene.add(this.group);
  }

  /** 10.9AR — "PEGADA DE FENDA" (pedido do Thiago: "tá toda escura"): rachaduras de energia no chão,
   * cristais flutuando em volta da grade, rasgos verticais de luz na borda, o OLHO da fenda (anéis
   * girando no céu) e luz roxa/ciano. Geometria/materiais compartilhados; só cristais e anéis animam. */
  private buildRift(c: typeof GameConfig.bossZone): void {
    const cx = c.center.x, cz = c.center.z, b = c.bounds.half;
    const rnd = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);
    // rachaduras (fora das salas)
    // 10.9BQ — energia/cristais da biblioteca; os que pulsam (pulseMats) são clones
    const crackMat = energyMat(0x9d6bff, "glow").clone();
    this.pulseMats.push(crackMat);
    const crackGeo = new THREE.BoxGeometry(1, 0.04, 0.14);
    for (let i = 0; i < 36; i++) {
      let x = cx, z = cz;
      for (let t = 0; t < 12; t++) { x = cx + rnd(-b + 3, b - 3); z = cz + rnd(-b + 3, b - 3); if (!this.roomAt(x, z)) break; }
      if (this.roomAt(x, z)) continue;
      const m = new THREE.Mesh(crackGeo, crackMat);
      m.scale.x = rnd(4, 14);
      m.position.set(x, 0.035, z);
      m.rotation.y = rnd(0, Math.PI);
      this.group.add(m);
    }
    // cristais flutuando (fora do miolo da grade)
    const shardGeo = new THREE.DodecahedronGeometry(1, 0);
    const shardMat = crystalStdMat(0x1a1230, 0x6a3bff, 0.4);
    for (let i = 0; i < 40; i++) {
      let x = cx, z = cz;
      for (let t = 0; t < 12; t++) { x = cx + rnd(-b + 2, b - 2); z = cz + rnd(-b + 2, b - 2); if (Math.abs(x - cx) > 40 || Math.abs(z - cz) > 26) break; }
      const m = new THREE.Mesh(shardGeo, shardMat);
      const s = rnd(0.5, 1.8);
      m.scale.set(s, s * rnd(0.7, 1.4), s);
      m.rotation.set(rnd(0, 3), rnd(0, 3), rnd(0, 3));
      const y0 = rnd(1.5, 9);
      m.position.set(x, y0, z);
      this.group.add(m);
      this.shards.push({ m, y0, ph: rnd(0, 6.28), spin: rnd(-0.4, 0.4) });
    }
    // rasgos verticais de luz na borda
    const tearMat = energyMat(0x2be0ff, "disc").clone();
    this.pulseMats.push(tearMat);
    const tearGeo = new THREE.PlaneGeometry(0.7, 11);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + rnd(-0.2, 0.2);
      const m = new THREE.Mesh(tearGeo, tearMat);
      m.position.set(cx + Math.cos(a) * (b - 4), 5.5, cz + Math.sin(a) * (b - 4));
      m.rotation.set(rnd(-0.15, 0.15), a + Math.PI / 2, rnd(-0.25, 0.25));
      this.group.add(m);
    }
    // o OLHO da fenda: anéis girando alto sobre a grade
    const ringMatA = energyMat(0x7c4dff, "glow"); // só giram: compartilhados
    const ringMatB = energyMat(0x2be0ff, "glow");
    const ringDefs: [number, number, THREE.MeshBasicMaterial][] = [[30, 0.5, ringMatA], [20, 0.3, ringMatB], [11, 0.22, ringMatA]];
    for (const [r, t, mat] of ringDefs) {
      const m = new THREE.Mesh(new THREE.TorusGeometry(r, t, 8, 56), mat);
      m.rotation.x = -Math.PI / 2 + rnd(-0.12, 0.12);
      m.position.set(cx, 26, cz);
      this.group.add(m);
      this.rings.push(m);
    }
    // luz roxa/ciano sobre a grade (2 pontos — 10.9BQ: mantidos, a zona é escura de propósito)
    const l1 = new THREE.PointLight(0x8f6bff, 2.2, 90);
    l1.position.set(cx - 22, 9, cz);
    this.group.add(l1);
    const l2 = new THREE.PointLight(0x2be0ff, 1.6, 90);
    l2.position.set(cx + 22, 9, cz + 8);
    this.group.add(l2);
  }

  private buildRoom(def: RoomDef, cx: number, cz: number): RoomView {
    const size = this.cfg.roomSize, half = size / 2;
    const rk = groundTextures("rock");
    const fm = rk.map.clone(); fm.repeat.set(Math.round(size / 5), Math.round(size / 5));
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x1c1a3c, roughness: 0.95, emissive: def.tint, emissiveIntensity: 0.08, map: fm }); // 10.9GQ
    if (quality.values.normalMaps) { const n = rk.normalMap.clone(); n.repeat.copy(fm.repeat); floorMat.normalMap = n; floorMat.normalScale.set(0.8, 0.8); }
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(size, size), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, 0.02, cz);
    floor.receiveShadow = true;
    this.group.add(floor);
    // 10.9CN — PAREDES ALTAS ao redor (a Fenda é uma arena selada), com um VÃO de porta na frente
    // (lado da chegada = z menor). Você entra pelo vão; ao desafiar, o PORTÃO de energia fecha o vão.
    const T = 0.5, WH = 2.6;              // espessura e altura da parede
    const doorHalf = size * 0.22;         // meia-largura do vão da porta
    const segW = half - doorHalf;         // largura de cada segmento da parede da frente
    const wallMat = crystalStdMat(0x2a2450, def.tint, 0.22).clone(); // 10.9HK — clone: ganha pedra com relevo
    applyWorldStone(wallMat, 0.22);
    const addWall = (px: number, pz: number, w: number, d: number): void => {
      const e = new THREE.Mesh(new THREE.BoxGeometry(w, WH, d), wallMat);
      e.position.set(px, WH / 2, pz);
      e.castShadow = true;
      this.group.add(e);
    };
    addWall(cx, cz + half, size + T, T);                              // fundo (z+)
    addWall(cx - half, cz, T, size + T);                             // esquerda
    addWall(cx + half, cz, T, size + T);                             // direita
    addWall(cx - (doorHalf + segW / 2), cz - half, segW, T);         // frente-esq
    addWall(cx + (doorHalf + segW / 2), cz - half, segW, T);         // frente-dir
    // PORTÃO de energia no vão — começa ABERTO (invisível); trancar torna visível e sólido
    const doorMat = energyMat(def.tint, "crystal").clone();
    const gate = new THREE.Mesh(new THREE.BoxGeometry(doorHalf * 2, WH, T), doorMat);
    gate.position.set(cx, WH / 2, cz - half);
    gate.visible = false;
    this.group.add(gate);
    // batente luminoso no chão (marca a soleira da porta)
    const sill = new THREE.Mesh(new THREE.BoxGeometry(doorHalf * 2, 0.06, 0.6), energyMat(def.tint, "crystal"));
    sill.position.set(cx, 0.05, cz - half);
    this.group.add(sill);
    const walls: Wall[] = [
      { axis: "z", pos: cz + half, lo: cx - half, hi: cx + half },       // fundo
      { axis: "x", pos: cx - half, lo: cz - half, hi: cz + half },       // esquerda
      { axis: "x", pos: cx + half, lo: cz - half, hi: cz + half },       // direita
      { axis: "z", pos: cz - half, lo: cx - half, hi: cx - doorHalf },   // frente-esq
      { axis: "z", pos: cz - half, lo: cx + doorHalf, hi: cx + half },   // frente-dir
    ];
    const gateWall: Wall = { axis: "z", pos: cz - half, lo: cx - doorHalf, hi: cx + doorHalf };
    // pedestal central (marca o ponto do boss)
    const ped = new THREE.Mesh(new THREE.RingGeometry(1.2, 1.6, 24), energyMat(def.tint, "ring"));
    ped.rotation.x = -Math.PI / 2;
    ped.position.set(cx, 0.04, cz);
    this.group.add(ped);
    const labelHolder = new THREE.Group();
    labelHolder.position.set(cx, 0, cz);
    this.group.add(labelHolder);
    return { def, cx, cz, half, floorMat, doorMat, labelHolder, label: null, labelKey: "", locked: false, gate, gateMat: doorMat, walls, gateWall };
  }

  setVisible(v: boolean): void { this.group.visible = v; }

  /** Sala que contém o ponto (ou null). */
  roomAt(x: number, z: number): RoomView | null {
    for (const r of this.rooms) if (Math.abs(x - r.cx) <= r.half && Math.abs(z - r.cz) <= r.half) return r;
    return null;
  }

  /** PORTA: empurra pra fora de sala cujo nível mínimo o player não tem. Retorna a sala bloqueada. */
  blockLocked(pos: THREE.Vector3, radius: number, level: number): RoomView | null {
    for (const r of this.rooms) {
      if (level >= r.def.level) continue;
      const dx = pos.x - r.cx, dz = pos.z - r.cz;
      const reach = r.half + radius;
      if (Math.abs(dx) >= reach || Math.abs(dz) >= reach) continue;
      // sai pelo eixo de MENOR penetração
      const px = reach - Math.abs(dx), pz = reach - Math.abs(dz);
      if (px < pz) pos.x = r.cx + Math.sign(dx || 1) * reach;
      else pos.z = r.cz + Math.sign(dz || 1) * reach;
      return r;
    }
    return null;
  }

  /** 10.9CN — tranca/destranca a PORTA da sala (portão de energia fecha o vão). */
  setLocked(room: RoomView, locked: boolean): void {
    room.locked = locked;
    room.gate.visible = locked;
  }
  /** Destranca todas as portas (ao sair da zona / morrer). */
  resetLocks(): void {
    for (const r of this.rooms) this.setLocked(r, false);
  }
  /** 10.9CN — colisão do player contra as paredes de TODAS as salas (o vão fica livre; o portão
   *  só bloqueia quando a sala está trancada). É o que faz "entrar pela porta e ficar preso". */
  resolveWalls(pos: THREE.Vector3, radius: number): void {
    for (const r of this.rooms) {
      for (const w of r.walls) this.pushWall(pos, radius, w);
      if (r.locked) this.pushWall(pos, radius, r.gateWall);
    }
  }
  private pushWall(p: THREE.Vector3, radius: number, w: Wall): void {
    const lim = 0.25 + radius; // meia-espessura + raio do player
    if (w.axis === "x") {
      if (p.z < w.lo - radius || p.z > w.hi + radius) return;
      const dx = p.x - w.pos;
      if (Math.abs(dx) < lim) p.x = w.pos + (dx >= 0 ? lim : -lim);
    } else {
      if (p.x < w.lo - radius || p.x > w.hi + radius) return;
      const dz = p.z - w.pos;
      if (Math.abs(dz) < lim) p.z = w.pos + (dz >= 0 ? lim : -lim);
    }
  }

  /** Durante a luta: mantém o player no círculo de luta da sala (sem fugir do boss). */
  clampToRoom(pos: THREE.Vector3, room: RoomView, radius: number): void {
    const lim = this.cfg.arenaRadius - radius - 0.3;
    const dx = pos.x - room.cx, dz = pos.z - room.cz;
    const d = Math.hypot(dx, dz);
    if (d > lim && d > 1e-4) { pos.x = room.cx + (dx / d) * lim; pos.z = room.cz + (dz / d) * lim; }
  }

  /** Etiquetas: 🔒 trancada (nível) · ⏳ renasce em X min · 👑 pronta. */
  refresh(level: number, nextAt: Record<string, number>, now: number, treeLocked?: (def: RoomDef) => boolean): void {
    for (const r of this.rooms) {
      const lvlLocked = level < r.def.level;
      // 10.9HW — sala de prestígio: nível ok mas Árvore do Void ainda incompleta (o rótulo mostra a %).
      const treeLock = !lvlLocked && !!treeLocked && treeLocked(r.def);
      const locked = lvlLocked || treeLock;
      const cdMs = Math.max(0, (nextAt[r.def.id] ?? 0) - now);
      const name = i18n.t(r.def.nameKey);
      let text: string;
      let color: string;
      if (lvlLocked) { text = `🔒 ${name} · Nv ${r.def.level}`; color = "#ff7a7a"; }
      else if (treeLock) { text = `🔒 ${name} · 🌳 ${Math.round((r.def.treePct ?? 0) * 100)}%`; color = "#ff9a5a"; }
      else if (cdMs > 0) { text = `⏳ ${name} · ${fmtDuration(cdMs)}`; color = "#c9c9d9"; }
      else { text = `👑 ${name} · Nv ${r.def.level}`; color = hex(r.def.tint); }
      r.floorMat.emissiveIntensity = locked ? 0.03 : cdMs > 0 ? 0.06 : 0.14;
      r.doorMat.color.setHex(lvlLocked ? 0xff4d4d : treeLock ? 0xff9a3c : r.def.tint);
      // 10.9BR — antes recriava os 9 sprites (canvas + textura) a cada chamada, sem dispose: vazamento.
      // Agora só quando o texto/cor mudou, e o antigo é descartado de verdade.
      const key = text + "|" + color;
      if (key === r.labelKey) continue;
      disposeLabel(r.label);
      r.label = makeLabel(text, color, 3.4);
      r.labelKey = key;
      r.labelHolder.add(r.label);
    }
  }

  update(dt: number): void {
    this.time += dt;
    const k = 0.55 + Math.sin(this.time * 2.4) * 0.25;
    for (const r of this.rooms) { r.doorMat.opacity = k; if (r.locked) r.gate.rotation.y = 0; }
    // 10.9AR — fenda viva: cristais flutuam/giram, o olho gira, rachaduras/rasgos/borda pulsam
    for (const s of this.shards) {
      s.m.position.y = s.y0 + Math.sin(this.time * 0.7 + s.ph) * 0.35;
      s.m.rotation.y += s.spin * dt;
    }
    for (let i = 0; i < this.rings.length; i++) this.rings[i].rotation.z += (i % 2 ? -0.12 : 0.08) * dt;
    const p = 0.42 + Math.sin(this.time * 1.7) * 0.18;
    for (const m of this.pulseMats) m.opacity = p;
  }
}
